<template>
	<div>
		<Sidebar v-model:visible="visible" class="side-bar-menu w-12 md:w-8 lg:w-5"
			@click.right="clickActionPreventDefault" @click.middle="clickActionPreventDefault"
			@touchstart="touchActionPreventDefault" @copy="clickActionPreventDefault"
			@dblclick="clickActionPreventDefault">
			<template #container="{ closeCallback }">
				<div class="flex flex-column h-full">
					<div class="flex align-items-center justify-content-between px-6 pt-3 mt-4 ml-3 flex-shrink-0">
						<span class="inline-flex align-items-center gap-2">
							<img alt="Vue logo" class="logo" src="@/assets/logo3.png" width="250" height="50" />
						</span>
						<span>
							<Button icon="pi pi-times" @click="closeCallback" severity="secondary" rounded
								class="h-5rem w-5rem" />
						</span>
					</div>
					<div class="overflow-y-auto">
						<ul class="list-none mt-6 px-3 m-0">
							<li @click="onMenuItemClick">
								<ul class="list-none p-0 m-0">
									<router-link v-for="(route, index) in routes" :key="index" :to="route.path"
										v-show="appStore.isRouteInMenu(route)" tag="li"
										:class="{ 'link-active': isLinkActive(route.path) }"
										class="cursor-pointer p-0 mx-2" active-class="" v-ripple>
										<a
											class="flex align-items-center p-3 border-round text-700 hover:surface-500 transition-duration-150 transition-colors p-ripple">
											<Avatar class="mr-4 category-avatar"
												:style="{ backgroundColor: isLinkActive(route.path) ? route.backgroundColor : 'transparent' }"
												size="xlarge" shape="circle">
												<i v-if="isLinkActive(route.path) && route.iconClass" :class="route.iconClass"></i>
												<i v-else-if="isLinkActive(route.path) && route.iconImage" class="pi pi-icon p-2">
													<img :src="route.iconImage" width="24" height="24" />
												</i>
											</Avatar>
											<span class="font-medium text-4xl">{{ route.meta.title }}</span>
											<Avatar v-if="route.additionalIcon" class="ml-auto category-avatar"
												:style="{ backgroundColor: 'transparent' }" size="xlarge"
												shape="circle">
												<i :class="route.additionalIcon" class="category-icon-2"></i>
											</Avatar>
										</a>
									</router-link>
								</ul>
							</li>
						</ul>
					</div>
				</div>
			</template>
		</Sidebar>
		<div class="logo-wrapper">
			<Button class="logo-button border-circle w-6rem sm:w-8rem md:w-10rem h-6rem sm:h-8rem md:h-10rem p-2 p-4 sm:mt-2 border-none"
				rounded outlined @click="onLogoClick"
				@pointerdown="onPressStart" @pointerup="onPressEnd"
				@touchstart.passive="onPressStart" @touchend="onPressEnd"
				@contextmenu.prevent @dragstart.prevent>
				<img alt="MidOpt logo" src="@/assets/logo.png" class="w-full h-full" draggable="false" />
			</Button>
			<RadialMenu :visible="radialVisible" :items="radialItems" @select="onRadialSelect"
				@close="radialVisible = false" />
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import Button from 'primevue/button';
import Sidebar from 'primevue/sidebar';
import router from './router/index'
import { useRoute } from "vue-router";
import { useAppStore } from "./stores/app";
import RadialMenu from './components/RadialMenu.vue';
import { clickActionPreventDefault, touchActionPreventDefault } from './services/screen';
import { isFullscreen, toggleFullscreen, onFullscreenChange } from './services/fullscreen';
import { preferences, zoomIn, zoomOut, resetZoom, zoomPercent, ZOOM_MIN, ZOOM_MAX } from './services/preferences';

// How long the logo must be held before the radial menu opens. Kept well under
// the kiosk browser's native long-press (~610ms, which fires pointerleave).
const LONG_PRESS_MS = 450;

const routes = ref([]);
const visible = ref(false);
const radialVisible = ref(false);
const fullscreen = ref(false);

let pressTimer = null;
let longPressed = false;
let unsubscribeFullscreen = () => {};

const route = useRoute();
const appStore = useAppStore();

// Radial menu options: zoom controls + fullscreen toggle. Recomputes so the
// zoom percentage / disabled states / fullscreen icon stay in sync.
const radialItems = computed(() => [
	{ key: 'zoom-out', icon: 'pi pi-search-minus', ariaLabel: 'Zoom out', disabled: preferences.zoom <= ZOOM_MIN },
	{ key: 'zoom-reset', label: `${zoomPercent()}%`, ariaLabel: `Reset zoom (currently ${zoomPercent()}%)` },
	{ key: 'zoom-in', icon: 'pi pi-search-plus', ariaLabel: 'Zoom in', disabled: preferences.zoom >= ZOOM_MAX },
	{
		key: 'fullscreen',
		icon: fullscreen.value ? 'pi pi-window-minimize' : 'pi pi-window-maximize',
		ariaLabel: fullscreen.value ? 'Exit full screen' : 'Enter full screen',
		active: fullscreen.value,
	},
]);

onMounted(() => {
	routes.value = router.options.routes;
	fullscreen.value = isFullscreen();
	// Keep the toggle (and persisted preference) in sync with the real state,
	// including when the user leaves fullscreen with the Esc key.
	unsubscribeFullscreen = onFullscreenChange(() => {
		fullscreen.value = isFullscreen();
		preferences.fullscreenPreferred = fullscreen.value;
	});
});

onUnmounted(() => unsubscribeFullscreen());

// --- Long-press detection on the logo -----------------------------------
// Pointer events unify mouse + touch; combined with `touch-action: none` and a
// non-interactive logo image this keeps the browser from hijacking the hold
// (native long-press / drag / selection fires pointercancel on Android).
const onPressStart = () => {
	longPressed = false;
	clearTimeout(pressTimer);
	pressTimer = setTimeout(() => {
		longPressed = true;
		radialVisible.value = true;
	}, LONG_PRESS_MS);
};

// Finger/mouse lifted — the tap itself is handled by @click. We intentionally
// do NOT clear on pointercancel/pointerleave/touchcancel: on a kiosk the only
// real end is lifting, and a spurious cancel was killing the hold timer.
const onPressEnd = () => {
	clearTimeout(pressTimer);
};

const onLogoClick = () => {
	// A long press just opened the radial menu — swallow the click.
	if (longPressed) {
		longPressed = false;
		return;
	}
	// A normal tap opens the sidebar (closing the radial menu if it was open).
	if (radialVisible.value) {
		radialVisible.value = false;
		return;
	}
	visible.value = true;
};

const onRadialSelect = (item) => {
	switch (item.key) {
		case 'zoom-in': zoomIn(); break;
		case 'zoom-out': zoomOut(); break;
		case 'zoom-reset': resetZoom(); break;
		case 'fullscreen':
			toggleFullscreen();
			radialVisible.value = false;
			break;
	}
};

const isLinkActive = (path) => {
	return route.path === path;
};

const onMenuItemClick = () => {
	visible.value = false;
	appStore.createEmailLoading = false;
	appStore.sendEmailBlocked = false;
	appStore.sendEmailRequireBlock = false;
};
</script>

<style>
/* Anchor for the radial menu — items are positioned around this centre. */
.logo-wrapper {
	position: relative;
	display: inline-block;
	-webkit-user-select: none;
	user-select: none;
	-webkit-touch-callout: none;
}

/* Own the touch so the browser doesn't fire its own long-press / scroll /
   drag (which cancels the hold timer on Android touchscreens). */
.logo-wrapper .logo-button {
	touch-action: none;
	-webkit-touch-callout: none;
}

/* Route all pointer events to the button, never the image (long-pressing an
   <img> triggers the native image menu / drag). */
.logo-wrapper .logo-button img {
	pointer-events: none;
	-webkit-user-drag: none;
	user-select: none;
}

.p-sidebar-visible {
	padding: .5rem;
}

.side-bar-menu {
	border-radius: 3.5rem;
	background-color: black;
}

.side-bar-menu * {
	color: white !important;
}

.side-bar-menu li.active * {
	color: white !important;
}

.side-bar-menu a {
	border-radius: 3rem !important;
}

.side-bar-menu>div>div>ul>li {
	color: black !important;
}

.side-bar-menu .pi-times:before {
	font-size: 2.5rem !important;
	font-weight: 900;
}

.side-bar-menu .category-icon:before {
	font-size: 1.8rem !important;
	color: black !important;
	font-weight: 900;
}

.side-bar-menu .category-icon-2:before {
	font-size: 1.8rem !important;
	color: black !important;
	font-weight: 900;
}

.side-bar-menu .link-active>a {
	background-color: #e0e0e0 !important;
}

.side-bar-menu .link-active * {
	color: #000000 !important;
}
</style>
